import React from "react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { Account, BalanceSnapshot } from "../../../types/spend-tracker";
import { formatCurrency } from "../../../utils/spend-calculations";

interface Entry {
  date: string;
  balances: BalanceSnapshot[];
  netWorth: number;
  totalCash: number;
  totalCredit: number;
  totalInvestments: number;
}

interface MonthlyStats {
  month: string;
  avgSpend: number;
  avgInvesting: number;
  creditChange: number;
  cashChange: number;
  investmentChange: number;
  netWorthChange: number;
  entries: number;
}

interface AnalyticsSectionProps {
  entries: Entry[];
  accounts: Account[];
}

const AnalyticsSection: React.FC<AnalyticsSectionProps> = ({
  entries,
  accounts,
}) => {
  const calculateMonthlyStats = (): MonthlyStats[] => {
    if (entries.length < 2) return [];

    const monthlyData = new Map<string, Entry[]>();

    // Group entries by month
    entries.forEach((entry) => {
      const date = new Date(entry.date);
      const monthKey = `${date.getFullYear()}-${String(
        date.getMonth() + 1
      ).padStart(2, "0")}`;
      if (!monthlyData.has(monthKey)) {
        monthlyData.set(monthKey, []);
      }
      monthlyData.get(monthKey)!.push(entry);
    });

    // Calculate stats for each month
    const stats: MonthlyStats[] = [];
    const sortedMonths = Array.from(monthlyData.keys()).sort();

    sortedMonths.forEach((month, index) => {
      const monthEntries = monthlyData.get(month)!;
      const firstEntry = monthEntries[0];
      const lastEntry = monthEntries[monthEntries.length - 1];

      // Get previous month's last entry for comparison
      let prevEntry: Entry | null = null;
      if (index > 0) {
        const prevMonthEntries = monthlyData.get(sortedMonths[index - 1])!;
        prevEntry = prevMonthEntries[prevMonthEntries.length - 1];
      } else if (monthEntries.length > 1) {
        // Use first entry of current month as "previous"
        prevEntry = firstEntry;
      }

      // Calculate changes
      const netWorthChange = prevEntry
        ? lastEntry.netWorth - prevEntry.netWorth
        : 0;
      const cashChange = prevEntry
        ? lastEntry.totalCash - prevEntry.totalCash
        : 0;
      const creditChange = prevEntry
        ? lastEntry.totalCredit - prevEntry.totalCredit
        : 0;
      const investmentChange = prevEntry
        ? lastEntry.totalInvestments - prevEntry.totalInvestments
        : 0;

      // Estimate spending (cash decrease + credit increase)
      const avgSpend = prevEntry
        ? Math.max(
            0,
            prevEntry.totalCash -
              lastEntry.totalCash +
              (lastEntry.totalCredit - prevEntry.totalCredit)
          )
        : 0;

      const avgInvesting = Math.max(0, investmentChange);

      stats.push({
        month: new Date(month + "-01").toLocaleDateString("en-US", {
          year: "numeric",
          month: "short",
        }),
        avgSpend,
        avgInvesting,
        creditChange,
        cashChange,
        investmentChange,
        netWorthChange,
        entries: monthEntries.length,
      });
    });

    return stats;
  };

  const calculateOverallStats = (monthlyStats: MonthlyStats[]) => {
    if (monthlyStats.length === 0) {
      return {
        avgMonthlySpend: 0,
        avgMonthlyInvesting: 0,
        avgMonthlyNetWorthChange: 0,
        totalNetWorthChange: 0,
        avgCreditChange: 0,
        avgCashChange: 0,
      };
    }

    const totalSpend = monthlyStats.reduce((sum, m) => sum + m.avgSpend, 0);
    const totalInvesting = monthlyStats.reduce(
      (sum, m) => sum + m.avgInvesting,
      0
    );
    const totalNetWorthChange = monthlyStats.reduce(
      (sum, m) => sum + m.netWorthChange,
      0
    );
    const totalCreditChange = monthlyStats.reduce(
      (sum, m) => sum + m.creditChange,
      0
    );
    const totalCashChange = monthlyStats.reduce(
      (sum, m) => sum + m.cashChange,
      0
    );

    const months = monthlyStats.length;

    return {
      avgMonthlySpend: totalSpend / months,
      avgMonthlyInvesting: totalInvesting / months,
      avgMonthlyNetWorthChange: totalNetWorthChange / months,
      totalNetWorthChange:
        entries[entries.length - 1].netWorth - entries[0].netWorth,
      avgCreditChange: totalCreditChange / months,
      avgCashChange: totalCashChange / months,
    };
  };

  if (entries.length < 2) {
    return (
      <div
        style={{
          backgroundColor: "#fff",
          borderRadius: "0.5rem",
          border: "1px solid #e5e7eb",
          padding: "2rem",
          textAlign: "center",
          marginBottom: "2rem",
        }}
      >
        <p style={{ color: "#888", margin: 0 }}>
          Add at least 2 entries to view analytics and trends
        </p>
      </div>
    );
  }

  const monthlyStats = calculateMonthlyStats();
  const overallStats = calculateOverallStats(monthlyStats);

  return (
    <div style={{ marginBottom: "2rem" }}>
      <h2
        style={{
          fontSize: "1.5rem",
          fontWeight: "600",
          marginBottom: "1.5rem",
        }}
      >
        📊 Analytics
      </h2>

      {/* Overall Stats Cards */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
          gap: "1rem",
          marginBottom: "2rem",
        }}
      >
        <div
          style={{
            backgroundColor: "#fff",
            borderRadius: "0.5rem",
            border: "1px solid #e5e7eb",
            padding: "1rem",
          }}
        >
          <p style={{ fontSize: "0.75rem", color: "#888", margin: 0 }}>
            Avg Monthly Spend
          </p>
          <p
            style={{
              fontSize: "1.5rem",
              fontWeight: "bold",
              color: "#ef4444",
              margin: "0.25rem 0 0 0",
            }}
          >
            {formatCurrency(overallStats.avgMonthlySpend)}
          </p>
        </div>

        <div
          style={{
            backgroundColor: "#fff",
            borderRadius: "0.5rem",
            border: "1px solid #e5e7eb",
            padding: "1rem",
          }}
        >
          <p style={{ fontSize: "0.75rem", color: "#888", margin: 0 }}>
            Avg Monthly Investing
          </p>
          <p
            style={{
              fontSize: "1.5rem",
              fontWeight: "bold",
              color: "#10b981",
              margin: "0.25rem 0 0 0",
            }}
          >
            {formatCurrency(overallStats.avgMonthlyInvesting)}
          </p>
        </div>

        <div
          style={{
            backgroundColor: "#fff",
            borderRadius: "0.5rem",
            border: "1px solid #e5e7eb",
            padding: "1rem",
          }}
        >
          <p style={{ fontSize: "0.75rem", color: "#888", margin: 0 }}>
            Avg Net Worth Change
          </p>
          <p
            style={{
              fontSize: "1.5rem",
              fontWeight: "bold",
              color:
                overallStats.avgMonthlyNetWorthChange >= 0
                  ? "#10b981"
                  : "#ef4444",
              margin: "0.25rem 0 0 0",
            }}
          >
            {formatCurrency(overallStats.avgMonthlyNetWorthChange)}
          </p>
        </div>

        <div
          style={{
            backgroundColor: "#fff",
            borderRadius: "0.5rem",
            border: "1px solid #e5e7eb",
            padding: "1rem",
          }}
        >
          <p style={{ fontSize: "0.75rem", color: "#888", margin: 0 }}>
            Total Change
          </p>
          <p
            style={{
              fontSize: "1.5rem",
              fontWeight: "bold",
              color:
                overallStats.totalNetWorthChange >= 0 ? "#10b981" : "#ef4444",
              margin: "0.25rem 0 0 0",
            }}
          >
            {formatCurrency(overallStats.totalNetWorthChange)}
          </p>
        </div>
      </div>

      {/* Charts */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(400px, 1fr))",
          gap: "1rem",
          marginBottom: "1rem",
        }}
      >
        {/* Monthly Spending and Investing Chart */}
        <div
          style={{
            backgroundColor: "#fff",
            borderRadius: "0.5rem",
            border: "1px solid #e5e7eb",
            padding: "1rem",
          }}
        >
          <h3
            style={{
              fontSize: "1rem",
              fontWeight: "600",
              marginBottom: "1rem",
            }}
          >
            Monthly Spending vs Investing
          </h3>
          <ResponsiveContainer width="100%" height={250}>
            <BarChart data={monthlyStats}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
              <XAxis dataKey="month" style={{ fontSize: "0.7rem" }} />
              <YAxis
                tickFormatter={(value) => `$${(value / 1000).toFixed(0)}k`}
                style={{ fontSize: "0.7rem" }}
              />
              <Tooltip
                formatter={(value: number) => formatCurrency(value)}
                contentStyle={{
                  backgroundColor: "#fff",
                  border: "1px solid #e5e7eb",
                  fontSize: "0.75rem",
                }}
              />
              <Legend wrapperStyle={{ fontSize: "0.75rem" }} />
              <Bar dataKey="avgSpend" fill="#ef4444" name="Spending" />
              <Bar dataKey="avgInvesting" fill="#10b981" name="Investing" />
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Account Balance Changes Chart */}
        <div
          style={{
            backgroundColor: "#fff",
            borderRadius: "0.5rem",
            border: "1px solid #e5e7eb",
            padding: "1rem",
          }}
        >
          <h3
            style={{
              fontSize: "1rem",
              fontWeight: "600",
              marginBottom: "1rem",
            }}
          >
            Monthly Balance Changes
          </h3>
          <ResponsiveContainer width="100%" height={250}>
            <LineChart data={monthlyStats}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
              <XAxis dataKey="month" style={{ fontSize: "0.7rem" }} />
              <YAxis
                tickFormatter={(value) => `$${(value / 1000).toFixed(0)}k`}
                style={{ fontSize: "0.7rem" }}
              />
              <Tooltip
                formatter={(value: number) => formatCurrency(value)}
                contentStyle={{
                  backgroundColor: "#fff",
                  border: "1px solid #e5e7eb",
                  fontSize: "0.75rem",
                }}
              />
              <Legend wrapperStyle={{ fontSize: "0.75rem" }} />
              <Line
                type="monotone"
                dataKey="cashChange"
                stroke="#10b981"
                strokeWidth={2}
                dot={{ fill: "#10b981", r: 3 }}
                name="Cash"
              />
              <Line
                type="monotone"
                dataKey="creditChange"
                stroke="#ef4444"
                strokeWidth={2}
                dot={{ fill: "#ef4444", r: 3 }}
                name="Credit"
              />
              <Line
                type="monotone"
                dataKey="investmentChange"
                stroke="#2563eb"
                strokeWidth={2}
                dot={{ fill: "#2563eb", r: 3 }}
                name="Investments"
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Individual Account Balances Over Time */}
      {(() => {
        // Sort entries by date ascending (oldest to newest) for proper left-to-right timeline
        const sortedEntries = [...entries].sort(
          (a, b) => new Date(a.date).getTime() - new Date(b.date).getTime()
        );

        // Prepare data for individual account tracking
        const accountBalanceData = sortedEntries.map((entry) => {
          const entryDate = new Date(entry.date);
          const dataPoint: any = {
            date: entryDate.toLocaleDateString("en-US", {
              year: "2-digit",
              month: "short",
              day: "numeric",
            }),
            timestamp: entryDate.getTime(), // For debugging
          };

          // Add each account's balance
          entry.balances.forEach((balance) => {
            const account = accounts.find((a) => a.id === balance.accountId);
            if (account) {
              // For credit cards, use absolute value for display (they're already negative)
              dataPoint[account.id] = balance.balance;
            }
          });

          return dataPoint;
        });

        const activeAccountsWithData = accounts.filter((account) =>
          entries.some((entry) =>
            entry.balances.some((b) => b.accountId === account.id)
          )
        );

        const cashAccounts = activeAccountsWithData.filter(
          (a) => a.type === "cash"
        );
        const creditAccounts = activeAccountsWithData.filter(
          (a) => a.type === "credit"
        );
        const investmentAccounts = activeAccountsWithData.filter(
          (a) => a.type === "investment"
        );

        const colors = [
          "#3b82f6", // blue
          "#10b981", // green
          "#f59e0b", // amber
          "#8b5cf6", // purple
          "#ec4899", // pink
          "#06b6d4", // cyan
          "#84cc16", // lime
          "#f97316", // orange
        ];

        return (
          <>
            {/* Cash & Investment Accounts */}
            {(cashAccounts.length > 0 || investmentAccounts.length > 0) && (
              <div
                style={{
                  backgroundColor: "#fff",
                  borderRadius: "0.5rem",
                  border: "1px solid #e5e7eb",
                  padding: "1rem",
                  marginBottom: "1rem",
                }}
              >
                <h3
                  style={{
                    fontSize: "1rem",
                    fontWeight: "600",
                    marginBottom: "1rem",
                  }}
                >
                  Cash & Investment Accounts Over Time
                </h3>
                <ResponsiveContainer width="100%" height={300}>
                  <LineChart data={accountBalanceData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                    <XAxis dataKey="date" style={{ fontSize: "0.7rem" }} />
                    <YAxis
                      tickFormatter={(value) =>
                        `$${(value / 1000).toFixed(0)}k`
                      }
                      style={{ fontSize: "0.7rem" }}
                    />
                    <Tooltip
                      formatter={(value: number) => formatCurrency(value)}
                      contentStyle={{
                        backgroundColor: "#fff",
                        border: "1px solid #e5e7eb",
                        fontSize: "0.75rem",
                      }}
                    />
                    <Legend wrapperStyle={{ fontSize: "0.75rem" }} />
                    {[...cashAccounts, ...investmentAccounts].map(
                      (account, index) => (
                        <Line
                          key={account.id}
                          type="monotone"
                          dataKey={account.id}
                          stroke={colors[index % colors.length]}
                          strokeWidth={2}
                          dot={{ r: 3 }}
                          name={account.name}
                        />
                      )
                    )}
                  </LineChart>
                </ResponsiveContainer>
              </div>
            )}

            {/* Credit Card Balances (Debt) */}
            {creditAccounts.length > 0 && (
              <div
                style={{
                  backgroundColor: "#fff",
                  borderRadius: "0.5rem",
                  border: "1px solid #e5e7eb",
                  padding: "1rem",
                  marginBottom: "1rem",
                }}
              >
                <h3
                  style={{
                    fontSize: "1rem",
                    fontWeight: "600",
                    marginBottom: "1rem",
                  }}
                >
                  Credit Card Balances (Debt)
                </h3>
                <ResponsiveContainer width="100%" height={300}>
                  <LineChart data={accountBalanceData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                    <XAxis dataKey="date" style={{ fontSize: "0.7rem" }} />
                    <YAxis
                      tickFormatter={(value) =>
                        `$${(Math.abs(value) / 1000).toFixed(0)}k`
                      }
                      style={{ fontSize: "0.7rem" }}
                    />
                    <Tooltip
                      formatter={(value: number) =>
                        formatCurrency(Math.abs(value))
                      }
                      contentStyle={{
                        backgroundColor: "#fff",
                        border: "1px solid #e5e7eb",
                        fontSize: "0.75rem",
                      }}
                    />
                    <Legend wrapperStyle={{ fontSize: "0.75rem" }} />
                    {creditAccounts.map((account, index) => (
                      <Line
                        key={account.id}
                        type="monotone"
                        dataKey={account.id}
                        stroke={["#ef4444", "#dc2626", "#f87171"][index % 3]}
                        strokeWidth={2}
                        dot={{ r: 3 }}
                        name={account.name}
                      />
                    ))}
                  </LineChart>
                </ResponsiveContainer>
                <p
                  style={{
                    fontSize: "0.75rem",
                    color: "#6b7280",
                    marginTop: "0.5rem",
                    fontStyle: "italic",
                  }}
                >
                  Note: Values shown as positive for readability. Lower values
                  are better (less debt).
                </p>
              </div>
            )}
          </>
        );
      })()}
    </div>
  );
};

export default AnalyticsSection;
